/**
 * GoPay MoR G11 — 18+ gate when Product.ageRestricted OR Tenant.reviewRisk=ELEVATED.
 * N/A only when both empty (no ageRestricted products AND reviewRisk ≠ ELEVATED)
 * AFTER seller-legal + catalog (or per-offer) inputs are resolved.
 * Fail-closed: unresolved inputs that may still require the gate → block add/pay.
 */

const AGE_GATE_ACK_PREFIX = 'customer:age-gate:ack:v1:';

export function isElevatedReviewRisk(reviewRisk: string | null | undefined): boolean {
  return typeof reviewRisk === 'string' && reviewRisk.trim().toUpperCase() === 'ELEVATED';
}

export function catalogRequiresAgeGate(
  products: readonly { ageRestricted?: boolean | null }[],
): boolean {
  return products.some((p) => p.ageRestricted === true);
}

/** OR of product ageRestricted ∪ tenant reviewRisk=ELEVATED. */
export function requiresAgeGate(input: {
  readonly products: readonly { ageRestricted?: boolean | null }[];
  readonly reviewRisk: string | null | undefined;
}): boolean {
  return catalogRequiresAgeGate(input.products) || isElevatedReviewRisk(input.reviewRisk);
}

export function ageGateStorageKey(tenantCode: string): string {
  return `${AGE_GATE_ACK_PREFIX}${tenantCode.trim().toLowerCase()}`;
}

export function readAgeGateAcknowledged(tenantCode: string): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  try {
    return sessionStorage.getItem(ageGateStorageKey(tenantCode)) === '1';
  } catch {
    return false;
  }
}

export function writeAgeGateAcknowledged(tenantCode: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(ageGateStorageKey(tenantCode), '1');
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Whether a React Query (or equivalent) dependency is ready to decide N/A vs required.
 * - Disabled → resolved (that leg is N/A for this surface).
 * - Enabled → only `isSuccess` counts as resolved.
 * - `isError` / pending → unresolved (fail-closed; never N/A-allow on empty/errored data).
 */
export function isAgeGateDependencyResolved(
  query: { readonly isSuccess: boolean; readonly isError: boolean },
  enabled: boolean,
): boolean {
  if (!enabled) {
    return true;
  }
  // G2 — isError is intentionally ignored; empty/errored data must not N/A-allow.
  return query.isSuccess;
}

/**
 * Browse / QueryClient cache settle for seller-legal age-gate.
 * Only `success` is ready — `error` / `pending` / undefined → fail-closed (G2).
 */
export function isSellerLegalCacheSuccessForAgeGate(
  status: string | undefined,
): boolean {
  return status === 'success';
}

export type CertifyAgeGateInput = {
  readonly tenantCode: string;
  /**
   * Catalog / offer products for the ageRestricted leg.
   * Ignored while `catalogResolved` is false.
   */
  readonly products: readonly { ageRestricted?: boolean | null }[] | null | undefined;
  /** false while catalog (or offer projection) may still reveal ageRestricted. */
  readonly catalogResolved: boolean;
  readonly reviewRisk: string | null | undefined;
  /** false while public seller-legal (reviewRisk) may still resolve to ELEVATED. */
  readonly sellerLegalResolved: boolean;
};

export type CertifyAgeGateResult =
  | { readonly ok: true; readonly required: boolean }
  | {
      readonly ok: false;
      readonly reason: 'unresolved' | 'not_acknowledged' | 'invalid_tenant';
    };

/**
 * Certify 18+ before cart add or pay/checkout.
 * - unresolved seller-legal or catalog → BLOCK (fail-closed; gate may be required)
 * - both resolved + not required → allow (N/A)
 * - required → allow only when `readAgeGateAcknowledged(tenant)` is true
 */
export function certifyAgeGate(input: CertifyAgeGateInput): CertifyAgeGateResult {
  const code = input.tenantCode.trim();
  if (code.length === 0) {
    return { ok: false, reason: 'invalid_tenant' };
  }
  if (!input.catalogResolved || !input.sellerLegalResolved) {
    return { ok: false, reason: 'unresolved' };
  }
  const products = input.products ?? [];
  const required = requiresAgeGate({
    products,
    reviewRisk: input.reviewRisk,
  });
  if (!required) {
    return { ok: true, required: false };
  }
  if (readAgeGateAcknowledged(code)) {
    return { ok: true, required: true };
  }
  return { ok: false, reason: 'not_acknowledged' };
}
